/**
 * UsuariosTab — Stitch-styled users table.
 *
 * Visual: white rounded-[24px] card on paper background.
 * Header: title + subtitle + "Crear usuario" dark pill.
 * Body: search input (underline style) + Filtros toggle + data table.
 * Columns: USUARIO (initials avatar + name), EMAIL, ROL (pill), ESTADO (dot), ACCIONES (hover reveal).
 * Footer: count + decorative pagination arrows.
 *
 * All service calls, validation, and state are unchanged from the previous implementation.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import {
  Search,
  SlidersHorizontal,
  Plus,
  Loader2,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { toast } from "sonner";
import { listUsuarios, createUsuario, updateUsuario } from "../services/usuarios.service.js";
import { friendlyMessage } from "@/shared/lib/errors.js";
import { ROL_LABEL, type UserRole, type Usuario } from "../types.js";
import { usePagination } from "../lib/usePagination.js";

// ─────────────────────────────────────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────────────────────────────────────

const ROLES: UserRole[] = ["technician", "admin"];

const FIELD_LABEL =
  "block font-mono text-[0.625rem] font-bold uppercase tracking-[0.15em] text-[#76746a] mb-2";

const UNDERLINE_INPUT =
  "w-full border-0 border-b border-[#ddd9cc] bg-transparent pb-2 text-sm text-[#1a1a18] outline-none placeholder:text-[#c5c2b8] focus:border-[#1a1a18] transition-colors";

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

/** Derive 2-char initials from a full name (first letter of first two words). */
function userInitials(nombre: string): string {
  const parts = nombre.trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0]![0]! + parts[1]![0]!).toUpperCase();
  return (parts[0]?.slice(0, 2) ?? "??").toUpperCase();
}

// ─────────────────────────────────────────────────────────────────────────────
// Form shape
// ─────────────────────────────────────────────────────────────────────────────

interface NuevoForm {
  nombre: string;
  email: string;
  password: string;
  rol: UserRole;
}

const FORM_VACIO: NuevoForm = { nombre: "", email: "", password: "", rol: "technician" };

// ─────────────────────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────────────────────

export function UsuariosTab() {
  const [usuarios, setUsuarios] = useState<Usuario[]>([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [editId, setEditId] = useState<string | null>(null);
  const [form, setForm] = useState<NuevoForm>(FORM_VACIO);
  const [guardando, setGuardando] = useState(false);
  /** Form panel: hidden until "Crear usuario" or row edit is triggered. */
  const [abierto, setAbierto] = useState(false);
  const [q, setQ] = useState("");
  const [filtroRol, setFiltroRol] = useState<UserRole | "todos">("todos");
  const [filtroEstado, setFiltroEstado] = useState<"todos" | "activos" | "inactivos">("todos");
  const [filtroAbierto, setFiltroAbierto] = useState(false);
  const filtroRef = useRef<HTMLDivElement>(null);

  const filtroActivo = filtroRol !== "todos" || filtroEstado !== "todos";

  const visibles = useMemo(() => {
    const t = q.trim().toLowerCase();
    return usuarios.filter((u) => {
      if (filtroRol !== "todos" && u.rol !== filtroRol) return false;
      if (filtroEstado === "activos" && !u.activo) return false;
      if (filtroEstado === "inactivos" && u.activo) return false;
      if (!t) return true;
      return u.nombre.toLowerCase().includes(t) || u.email.toLowerCase().includes(t);
    });
  }, [usuarios, q, filtroRol, filtroEstado]);

  const { pageItems, page, totalPages, rangeStart, rangeEnd, prev, next } =
    usePagination(visibles);

  useEffect(() => {
    if (!filtroAbierto) return;
    function handleOutside(e: MouseEvent) {
      if (filtroRef.current && !filtroRef.current.contains(e.target as Node)) {
        setFiltroAbierto(false);
      }
    }
    document.addEventListener("mousedown", handleOutside);
    return () => document.removeEventListener("mousedown", handleOutside);
  }, [filtroAbierto]);

  async function recargar() {
    setCargando(true);
    setError(null);
    try {
      setUsuarios(await listUsuarios());
    } catch (err) {
      setError(friendlyMessage(err));
    } finally {
      setCargando(false);
    }
  }

  useEffect(() => {
    void recargar();
  }, []);

  const enEdicion = editId !== null;

  // Validation: returns the first problem, or null if everything is OK.
  // In edit mode, password is optional (empty = no change); if provided, must be ≥8.
  function validar(): string | null {
    if (!form.nombre.trim()) return "El nombre es obligatorio.";
    if (!form.email.trim()) return "El correo es obligatorio.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim()))
      return "El correo no tiene un formato válido.";
    const passwordRequerida = !enEdicion || form.password.length > 0;
    if (passwordRequerida && form.password.length < 8)
      return "La contraseña debe tener al menos 8 caracteres.";
    return null;
  }

  function resetForm() {
    setEditId(null);
    setForm(FORM_VACIO);
    setAbierto(false);
  }

  function startEdit(u: Usuario) {
    setEditId(u.id);
    setForm({ nombre: u.nombre, email: u.email, password: "", rol: u.rol });
    setError(null);
    setAbierto(true);
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    const problema = validar();
    if (problema) {
      toast.error(problema);
      return;
    }
    setGuardando(true);
    setError(null);
    try {
      if (editId) {
        await updateUsuario(editId, {
          nombre: form.nombre.trim(),
          email: form.email.trim(),
          rol: form.rol,
          ...(form.password.length >= 8 ? { password: form.password } : {}),
        });
        toast.success("Usuario actualizado");
      } else {
        await createUsuario({
          nombre: form.nombre.trim(),
          email: form.email.trim(),
          password: form.password,
          rol: form.rol,
        });
        toast.success("Usuario creado");
      }
      resetForm();
      await recargar();
    } catch (err) {
      toast.error(friendlyMessage(err));
    } finally {
      setGuardando(false);
    }
  }

  async function toggleActivo(u: Usuario) {
    try {
      await updateUsuario(u.id, { activo: !u.activo });
      toast.success(u.activo ? "Usuario desactivado" : "Usuario activado");
      await recargar();
    } catch (err) {
      toast.error(friendlyMessage(err));
    }
  }

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <div className="w-full max-w-[1760px] bg-white rounded-[24px] border border-[#ddd9cc] shadow-sm flex flex-col">

      {/* ── Card header ──────────────────────────────────────────────────────── */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between p-4 pb-4 sm:p-8 sm:pb-6">
        <div>
          <h1 className="text-[#1a1a18] text-[1.75rem] font-bold leading-tight mb-2">
            Gestión de Usuarios
          </h1>
          <p className="text-[#76746a] text-sm leading-relaxed">
            Administra los accesos y roles del equipo.
          </p>
        </div>
        <button
          type="button"
          onClick={() => {
            if (abierto) resetForm();
            else {
              resetForm();
              setAbierto(true);
            }
          }}
          className="flex items-center justify-center gap-2 bg-[#1a1a18] text-white rounded-[20px] px-6 py-3 font-bold text-sm w-full sm:w-auto mt-4 sm:mt-0 sm:ml-6 hover:bg-[#2a2a26] transition-colors"
        >
          <Plus className="size-4" aria-hidden />
          {abierto ? "Cerrar" : "Crear usuario"}
        </button>
      </div>
      <div className="border-b border-[#ddd9cc]" />

      {/* ── Card body ────────────────────────────────────────────────────────── */}
      <div className="p-4 sm:p-8 flex-1">

        {/* Inline create / edit form */}
        {abierto && (
          <form onSubmit={onSubmit} className="bg-[#f4f3ee] rounded-xl p-6 mb-6">
            <span className="block font-mono text-[0.625rem] font-bold uppercase tracking-[0.15em] text-[#76746a] mb-4">
              {enEdicion ? "Editar usuario" : "Nuevo usuario"}
            </span>
            <div className="grid gap-6 sm:grid-cols-2">
              <div>
                <label htmlFor="u-nombre" className={FIELD_LABEL}>
                  Nombre
                </label>
                <input
                  id="u-nombre"
                  type="text"
                  value={form.nombre}
                  onChange={(e) => setForm({ ...form, nombre: e.target.value })}
                  placeholder="Juan Pérez"
                  className={UNDERLINE_INPUT}
                />
              </div>
              <div>
                <label htmlFor="u-email" className={FIELD_LABEL}>
                  Email
                </label>
                <input
                  id="u-email"
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder="juan@empresa.com"
                  className={UNDERLINE_INPUT}
                />
              </div>
              <div>
                <label htmlFor="u-pass" className={FIELD_LABEL}>
                  Contraseña
                </label>
                <input
                  id="u-pass"
                  type="password"
                  autoComplete="new-password"
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  placeholder={enEdicion ? "dejar vacío = sin cambios" : "mín. 8 caracteres"}
                  className={UNDERLINE_INPUT}
                />
              </div>
              <div>
                <label htmlFor="u-rol" className={FIELD_LABEL}>
                  Rol
                </label>
                <select
                  id="u-rol"
                  value={form.rol}
                  onChange={(e) => setForm({ ...form, rol: e.target.value as UserRole })}
                  className={UNDERLINE_INPUT}
                >
                  {ROLES.map((r) => (
                    <option key={r} value={r}>
                      {ROL_LABEL[r]}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="mt-6 flex items-center gap-3">
              <button
                type="submit"
                disabled={guardando}
                className="bg-[#1a1a18] text-white rounded-[20px] px-5 py-2.5 text-sm font-bold hover:bg-[#2a2a26] transition-colors disabled:opacity-60 disabled:pointer-events-none"
              >
                {guardando
                  ? "Guardando…"
                  : enEdicion
                    ? "Guardar cambios"
                    : "Crear usuario"}
              </button>
              <button
                type="button"
                onClick={resetForm}
                className="border border-[#ddd9cc] rounded-[20px] px-5 py-2.5 text-sm font-bold text-[#1a1a18] hover:bg-white transition-colors"
              >
                {enEdicion ? "Cancelar" : "Cerrar"}
              </button>
            </div>
          </form>
        )}

        {error && <p className="mb-4 text-sm text-red-600">{error}</p>}

        {/* Search + Filtros */}
        <div className="flex items-center gap-3 mb-6">
          <div className="relative flex-1 sm:flex-none sm:w-64">
            <Search
              className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-[#76746a]"
              aria-hidden
            />
            <input
              type="search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Buscar usuario..."
              className="w-full pl-10 pr-4 py-2 bg-[#f4f3ee] border-b border-[#ddd9cc] text-sm text-[#1a1a18] outline-none placeholder:text-[#c5c2b8] focus:border-[#1a1a18] transition-colors"
            />
          </div>
          <div ref={filtroRef} className="relative">
            <button
              type="button"
              onClick={() => setFiltroAbierto((v) => !v)}
              className={[
                "flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors",
                filtroActivo
                  ? "border-[#1a1a18] text-[#1a1a18] bg-[#f4f3ee]"
                  : "border-[#ddd9cc] text-[#76746a] hover:bg-[#f4f3ee]",
              ].join(" ")}
            >
              <SlidersHorizontal className="size-4" aria-hidden />
              <span className="font-mono text-[0.625rem] font-bold uppercase tracking-[0.12em]">
                Filtros
              </span>
              {filtroActivo && (
                <span className="w-2 h-2 rounded-full bg-[#f59e0b] shrink-0" aria-hidden />
              )}
            </button>

            {filtroAbierto && (
              <div className="absolute right-0 top-full mt-2 z-20 bg-white border border-[#ddd9cc] rounded-xl shadow-sm p-4 w-52">
                {/* Rol */}
                <p className="block font-mono text-[0.625rem] font-bold uppercase tracking-[0.15em] text-[#76746a] mb-2">
                  Rol
                </p>
                <div className="flex flex-col gap-0.5 mb-4">
                  {(["todos", ...ROLES] as Array<UserRole | "todos">).map((r) => (
                    <button
                      key={r}
                      type="button"
                      onClick={() => setFiltroRol(r)}
                      className={[
                        "text-left text-sm px-2 py-1.5 rounded-lg transition-colors",
                        filtroRol === r
                          ? "bg-[#1a1a18] text-white"
                          : "text-[#1a1a18] hover:bg-[#f4f3ee]",
                      ].join(" ")}
                    >
                      {r === "todos" ? "Todos" : ROL_LABEL[r]}
                    </button>
                  ))}
                </div>

                {/* Estado */}
                <p className="block font-mono text-[0.625rem] font-bold uppercase tracking-[0.15em] text-[#76746a] mb-2">
                  Estado
                </p>
                <div className="flex flex-col gap-0.5 mb-4">
                  {(["todos", "activos", "inactivos"] as const).map((s) => (
                    <button
                      key={s}
                      type="button"
                      onClick={() => setFiltroEstado(s)}
                      className={[
                        "text-left text-sm px-2 py-1.5 rounded-lg transition-colors capitalize",
                        filtroEstado === s
                          ? "bg-[#1a1a18] text-white"
                          : "text-[#1a1a18] hover:bg-[#f4f3ee]",
                      ].join(" ")}
                    >
                      {s === "todos" ? "Todos" : s.charAt(0).toUpperCase() + s.slice(1)}
                    </button>
                  ))}
                </div>

                {filtroActivo && (
                  <button
                    type="button"
                    onClick={() => { setFiltroRol("todos"); setFiltroEstado("todos"); }}
                    className="text-xs text-[#76746a] hover:text-[#1a1a18] transition-colors underline"
                  >
                    Limpiar
                  </button>
                )}
              </div>
            )}
          </div>
        </div>

        {/* ── Content states ──────────────────────────────────────────────── */}
        {cargando ? (
          <div className="flex items-center justify-center gap-2 py-12 text-sm text-[#76746a]">
            <Loader2 className="size-4 animate-spin" aria-hidden />
            Cargando usuarios…
          </div>
        ) : usuarios.length === 0 ? (
          <p className="py-12 text-center text-sm text-[#76746a]">
            No hay usuarios todavía.
          </p>
        ) : visibles.length === 0 ? (
          <p className="py-12 text-center text-sm text-[#76746a]">
            Sin resultados para «{q}».
          </p>
        ) : (
          <>
            {/* Mobile: card list */}
            <ul className="flex flex-col divide-y divide-[#ddd9cc] md:hidden">
              {pageItems.map((u) => (
                <li
                  key={u.id}
                  className={[
                    "flex items-start gap-3 py-4",
                    !u.activo && "opacity-60",
                  ]
                    .filter(Boolean)
                    .join(" ")}
                >
                  <div className="w-10 h-10 rounded-full bg-[#e5e2e1] flex items-center justify-center font-bold text-[#1a1a18] text-sm shrink-0">
                    {userInitials(u.nombre)}
                  </div>
                  <div className="min-w-0 flex-1 space-y-1">
                    <p className="font-bold text-[#1a1a18] text-sm truncate">{u.nombre}</p>
                    <p className="text-xs text-[#76746a] truncate">{u.email}</p>
                    <div className="flex flex-wrap items-center gap-2 pt-1">
                      <span className="font-mono text-[0.6rem] font-bold uppercase tracking-[0.12em] text-[#76746a] bg-[#f1edec] border border-[#ddd9cc] rounded-full px-2.5 py-0.5">
                        {ROL_LABEL[u.rol]}
                      </span>
                      <span className="flex items-center gap-1.5 font-mono text-[0.6rem] font-bold uppercase tracking-[0.12em]">
                        <span
                          aria-hidden
                          className="w-2 h-2 rounded-full"
                          style={{ backgroundColor: u.activo ? "#10b981" : "#777771" }}
                        />
                        <span className={u.activo ? "text-[#1a1a18]" : "text-[#76746a]"}>
                          {u.activo ? "Activo" : "Inactivo"}
                        </span>
                      </span>
                    </div>
                    <div className="flex flex-wrap gap-1.5 pt-2">
                      <button
                        type="button"
                        onClick={() => startEdit(u)}
                        className="text-xs border border-[#ddd9cc] rounded-[12px] px-3 py-1 font-bold text-[#1a1a18] hover:bg-[#f4f3ee] transition-colors"
                      >
                        Editar
                      </button>
                      {u.activo ? (
                        <button
                          type="button"
                          onClick={() => void toggleActivo(u)}
                          className="text-xs border border-[#ddd9cc] rounded-[12px] px-3 py-1 font-bold text-[#76746a] hover:bg-[#f4f3ee] transition-colors"
                        >
                          Desactivar
                        </button>
                      ) : (
                        <button
                          type="button"
                          onClick={() => void toggleActivo(u)}
                          className="text-xs border border-[#ddd9cc] rounded-[12px] px-3 py-1 font-bold text-[#1a1a18] hover:bg-[#f4f3ee] transition-colors"
                        >
                          Activar
                        </button>
                      )}
                    </div>
                  </div>
                </li>
              ))}
            </ul>

            {/* Desktop: table */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full min-w-[40rem]">
                <thead>
                  <tr className="border-b border-[#ddd9cc]">
                    <th className="pb-4 text-left pl-4 w-[35%] font-mono text-[0.625rem] font-bold uppercase tracking-[0.12em] text-[#76746a]">
                      Usuario
                    </th>
                    <th className="pb-4 text-left w-[28%] font-mono text-[0.625rem] font-bold uppercase tracking-[0.12em] text-[#76746a]">
                      Email
                    </th>
                    <th className="pb-4 text-left w-[14%] font-mono text-[0.625rem] font-bold uppercase tracking-[0.12em] text-[#76746a]">
                      Rol
                    </th>
                    <th className="pb-4 text-left w-[13%] font-mono text-[0.625rem] font-bold uppercase tracking-[0.12em] text-[#76746a]">
                      Estado
                    </th>
                    <th className="pb-4 text-right pr-4 w-[10%] font-mono text-[0.625rem] font-bold uppercase tracking-[0.12em] text-[#76746a]">
                      Acciones
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {pageItems.map((u) => (
                    <tr
                      key={u.id}
                      className={[
                        "border-b border-[#ddd9cc] last:border-0 hover:bg-[#f4f3ee] transition-colors group",
                        !u.activo && "opacity-60",
                      ]
                        .filter(Boolean)
                        .join(" ")}
                    >
                      {/* USUARIO */}
                      <td className="py-4 pl-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-[#e5e2e1] flex items-center justify-center font-bold text-[#1a1a18] text-sm shrink-0">
                            {userInitials(u.nombre)}
                          </div>
                          <span className="font-bold text-[#1a1a18] text-sm">{u.nombre}</span>
                        </div>
                      </td>

                      {/* EMAIL */}
                      <td className="py-4 text-sm text-[#76746a] pr-2">
                        <span className="block truncate">{u.email}</span>
                      </td>

                      {/* ROL */}
                      <td className="py-4">
                        <span className="inline-flex font-mono text-[0.6rem] font-bold uppercase tracking-[0.12em] text-[#76746a] bg-[#f1edec] border border-[#ddd9cc] rounded-full px-3 py-1">
                          {ROL_LABEL[u.rol]}
                        </span>
                      </td>

                      {/* ESTADO */}
                      <td className="py-4">
                        <span className="flex items-center gap-2 font-mono text-[0.6rem] font-bold uppercase tracking-[0.12em]">
                          <span
                            aria-hidden
                            className="w-2 h-2 rounded-full shrink-0"
                            style={{ backgroundColor: u.activo ? "#10b981" : "#777771" }}
                          />
                          <span className={u.activo ? "text-[#1a1a18]" : "text-[#76746a]"}>
                            {u.activo ? "Activo" : "Inactivo"}
                          </span>
                        </span>
                      </td>

                      {/* ACCIONES — hover reveal */}
                      <td className="py-4 pr-4">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            type="button"
                            onClick={() => startEdit(u)}
                            className="text-xs border border-[#ddd9cc] rounded-[12px] px-3 py-1 font-bold text-[#1a1a18] hover:bg-white transition-colors"
                          >
                            Editar
                          </button>
                          {u.activo ? (
                            <button
                              type="button"
                              onClick={() => void toggleActivo(u)}
                              className="text-xs border border-[#ddd9cc] rounded-[12px] px-3 py-1 font-bold text-[#76746a] hover:bg-white transition-colors"
                            >
                              Desactivar
                            </button>
                          ) : (
                            <button
                              type="button"
                              onClick={() => void toggleActivo(u)}
                              className="text-xs border border-[#ddd9cc] rounded-[12px] px-3 py-1 font-bold text-[#1a1a18] hover:bg-white transition-colors"
                            >
                              Activar
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      {/* ── Card footer ──────────────────────────────────────────────────────── */}
      <div className="px-4 py-3 sm:px-8 sm:py-4 border-t border-[#ddd9cc] flex items-center justify-between">
        <span className="font-mono text-[0.625rem] font-bold uppercase tracking-[0.12em] text-[#76746a]">
          {cargando
            ? ""
            : `Mostrando ${rangeStart}–${rangeEnd} de ${usuarios.length} usuarios`}
        </span>
        <div className="flex items-center gap-2">
          {totalPages > 1 && (
            <span className="font-mono text-[0.625rem] font-bold uppercase tracking-[0.12em] text-[#76746a]">
              {`${page} / ${totalPages}`}
            </span>
          )}
          <button
            type="button"
            disabled={page <= 1}
            onClick={prev}
            aria-label="Página anterior"
            className="p-1 rounded hover:bg-[#f4f3ee] transition-colors disabled:opacity-30"
          >
            <ChevronLeft className="size-5 text-[#76746a]" aria-hidden />
          </button>
          <button
            type="button"
            disabled={page >= totalPages}
            onClick={next}
            aria-label="Página siguiente"
            className="p-1 rounded hover:bg-[#f4f3ee] transition-colors disabled:opacity-30"
          >
            <ChevronRight className="size-5 text-[#76746a]" aria-hidden />
          </button>
        </div>
      </div>
    </div>
  );
}
